import React, { useState, useMemo, useEffect } from "react";
import FileInput from "@amzn/meridian/file-input";
import Button from "@amzn/meridian/button";
import Alert from "@amzn/meridian/alert";
import Text from "@amzn/meridian/text";
import Row from "@amzn/meridian/row";
import Column from "@amzn/meridian/column";
import Modal from "@amzn/meridian/modal";
import Heading from "@amzn/meridian/heading";
import downloadTokens from "@amzn/meridian-tokens/base/icon/download-small";
import Icon from "@amzn/meridian/icon";
import {
  validateAndUpsertModel,
  convertCsvToHierarchyJson,
  LabelHierarchy,
} from "src/store/modelManagementSlice";
import { useAppDispatch, useAppSelector } from "src/store/store";
import { createToast } from "src/store/toastsSlice";
import { TOAST_TIMEOUT } from "src/config/Toast";
import { isAdminUser } from "src/utils/permissionUtil";
import "./labelHierarchyModal.scss";

interface LabelHierarchyModalProps {
  modelId: string;
  isOpen: boolean;
  onClose: () => void;
  disabled?: boolean;
}

const LabelHierarchyModal: React.FC<LabelHierarchyModalProps> = ({
  modelId,
  isOpen,
  onClose,
  disabled = false,
}) => {
  const dispatch = useAppDispatch();
  const { selectedModel } = useAppSelector((state) => state.modelSlice);
  const { ldap } = useAppSelector((state) => state.user);
  const isAdminOrEngineer = isAdminUser(ldap);
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"upload" | "view">("upload");
  const [activeHierarchyIndex, setActiveHierarchyIndex] = useState(0);
  const [previewData, setPreviewData] = useState<{ [key: string]: any }[] | null>(null);
  const [previewCounts, setPreviewCounts] = useState<{
    totalRows: number;
    uniqueParentFields: string[];
    uniqueChildFields: string[];
    parentChildPairs: { [key: string]: string };
  } | null>(null);
  const [convertedLabelHierarchy, setConvertedLabelHierarchy] = useState<LabelHierarchy[] | null>(
    null,
  );

  // Reset the active index when switching views or loading new preview data
  useEffect(() => {
    setActiveHierarchyIndex(0);
  }, [activeTab, previewData]);

  // Define types for hierarchy display
  type RelationshipItem = {
    parentOption: string;
    childOptions: string[];
  };

  type HierarchyItem = {
    parentFieldName: string;
    childFieldName: string;
    relationships: RelationshipItem[];
  };

  type HierarchyMessage = {
    message: string;
  };

  type FormattedHierarchy = HierarchyItem[] | [HierarchyMessage];

  // Format hierarchy data for display
  const formattedHierarchy: FormattedHierarchy = useMemo(() => {
    if (
      !selectedModel?.hierarchy ||
      !Array.isArray(selectedModel.hierarchy) ||
      selectedModel.hierarchy.length === 0
    ) {
      return [{ message: "No hierarchy data available for this model." }];
    }

    return selectedModel.hierarchy.map((hierarchy) => {
      const { parentFieldName, childFieldName, options } = hierarchy;

      // Format each parent-child relationship
      const relationships: RelationshipItem[] = Object.entries(options).map(
        ([parentOption, childOptions]) => ({
          parentOption,
          childOptions,
        }),
      );

      return {
        parentFieldName,
        childFieldName,
        relationships,
      };
    });
  }, [selectedModel?.hierarchy]);

  // Check if hierarchy data is empty
  const isEmptyHierarchy = useMemo(() => {
    if (!selectedModel?.hierarchy) return true;
    return (
      formattedHierarchy.length === 0 ||
      (formattedHierarchy.length === 1 && "message" in formattedHierarchy[0])
    );
  }, [selectedModel?.hierarchy, formattedHierarchy]);

  // Set the default view based on whether hierarchy exists and user permissions
  useEffect(() => {
    if (isOpen) {
      if (isEmptyHierarchy) {
        // If no hierarchy data exists and user is not admin/engineer, they won't be able to create one
        // so just show the empty view state
        setActiveTab(isAdminOrEngineer ? "upload" : "view");
      } else {
        setActiveTab("view");
      }
    }
  }, [isOpen, isEmptyHierarchy, isAdminOrEngineer]);

  const parseCSVFile = (file: File): Promise<string[][]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;

          // Skip comment lines and split by rows
          const rows = text
            .split("\n")
            .filter((line) => !line.trim().startsWith("#") && line.trim().length > 0)
            .map((line) => {
              // Handle quoted values correctly
              const result = [];
              let inQuotes = false;
              let currentValue = "";

              for (let i = 0; i < line.length; i++) {
                const char = line[i];

                if (char === '"' && (i === 0 || line[i - 1] !== "\\")) {
                  inQuotes = !inQuotes;
                } else if (char === "," && !inQuotes) {
                  result.push(currentValue.trim());
                  currentValue = "";
                } else {
                  currentValue += char;
                }
              }

              if (currentValue.trim()) {
                result.push(currentValue.trim());
              }

              return result;
            });

          resolve(rows);
        } catch (error) {
          reject(error);
        }
      };

      reader.onerror = () => {
        reject(new Error("Error reading file"));
      };

      reader.readAsText(file);
    });
  };

  const processCSVData = async (file: File) => {
    try {
      const rows = await parseCSVFile(file);

      if (rows.length < 2) {
        setError("CSV file is empty or has only headers");
        setPreviewData(null);
        setConvertedLabelHierarchy(null);
        return;
      }

      // Extract header and data rows
      const headers = rows[0];
      const dataRows = rows.slice(1);

      // Validate headers
      if (
        headers.length !== 4 ||
        !headers.includes("parentFieldName") ||
        !headers.includes("childFieldName") ||
        !headers.includes("parentOption") ||
        !headers.includes("childOption")
      ) {
        setError(
          "CSV headers must include: parentFieldName, childFieldName, parentOption, childOption",
        );
        setPreviewData(null);
        setConvertedLabelHierarchy(null);
        return;
      }

      // Convert to array of objects
      const parsedData = dataRows.map((row) => {
        const item: { [key: string]: string } = {};
        headers.forEach((header, index) => {
          if (index < row.length) {
            item[header] = row[index];
          }
        });
        return item;
      });

      // Analyze data for preview - use Array.filter for uniqueness instead of Set to avoid TS issues
      const uniqueParentFields = parsedData
        .map((item) => item.parentFieldName)
        .filter((value, index, self) => self.indexOf(value) === index);

      const uniqueChildFields = parsedData
        .map((item) => item.childFieldName)
        .filter((value, index, self) => self.indexOf(value) === index);

      // Create parent-child field pairs
      const parentChildPairs: { [key: string]: string } = {};
      parsedData.forEach((item) => {
        if (item.parentFieldName && item.childFieldName) {
          parentChildPairs[item.parentFieldName] = item.childFieldName;
        }
      });

      // Set preview data
      setPreviewData(parsedData);
      setPreviewCounts({
        totalRows: parsedData.length,
        uniqueParentFields,
        uniqueChildFields,
        parentChildPairs,
      });

      // Convert to hierarchy JSON structure for direct API upload
      const labelHierarchy = convertCsvToHierarchyJson(parsedData);
      setConvertedLabelHierarchy(labelHierarchy);

      // If there were previous errors, clear them
      setError(null);
    } catch (error) {
      console.error("Error processing CSV:", error);
      setError(
        `Failed to parse CSV file: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
      setPreviewData(null);
      setConvertedLabelHierarchy(null);
    }
  };

  const handleFileChange = (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setFile(file);
      processCSVData(file);
    }
  };

  const handleUpload = async () => {
    if (!file || !convertedLabelHierarchy) {
      setError("Please select a valid CSV file to upload");
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      // First get the current model
      if (!selectedModel) {
        throw new Error("Model not found");
      }

      // Update only the hierarchy field
      const updatedModel = {
        ...selectedModel,
        hierarchy: convertedLabelHierarchy,
      };

      // Use the standard model update endpoint
      await dispatch(validateAndUpsertModel(updatedModel)).unwrap();

      setFile(null);
      setPreviewData(null);
      setConvertedLabelHierarchy(null);

      dispatch(
        createToast({
          type: "success",
          message: "Label hierarchy uploaded successfully",
          timeout: TOAST_TIMEOUT,
        }),
      );
      onClose(); // Close the modal on success
    } catch (error: any) {
      console.error("Upload error:", error);
      setError(error.message || "Failed to upload label hierarchy");
      dispatch(
        createToast({
          type: "error",
          message: `Upload failed: ${error.message || "Unknown error"}`,
          timeout: TOAST_TIMEOUT,
        }),
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownloadTemplate = () => {
    // Create a CSV template with headers and instructions
    const instructions = [
      "# Label Hierarchy CSV Format",
      "# Each row defines one child option for a parent-child relationship",
      "# Example: CategoryType,SubCategory,Hardware,Laptop",
      "#",
      "# Multiple rows with the same parent option will define multiple child options for that parent",
      "# parentFieldName and childFieldName should match label names in your model",
      "#",
    ];
    const csvHeader = "parentFieldName,childFieldName,parentOption,childOption";
    const csvContent = [...instructions, csvHeader].join("\n");

    // Create and download the template file
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "label_hierarchy_template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadHierarchyCSV = () => {
    if (isEmptyHierarchy || !selectedModel?.hierarchy) {
      dispatch(
        createToast({
          type: "error",
          message: "No hierarchy data available to download",
          timeout: TOAST_TIMEOUT,
        }),
      );
      return;
    }

    try {
      // Convert hierarchy data to CSV format
      const rows: string[] = ["parentFieldName,childFieldName,parentOption,childOption"];

      // Helper function to escape CSV values
      const escapeCSVValue = (value: string) => {
        // If value contains comma, newline or quotes, wrap it in quotes and escape any quotes inside
        if (value.includes(",") || value.includes("\n") || value.includes('"')) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      };

      selectedModel.hierarchy.forEach((hierarchy) => {
        const { parentFieldName, childFieldName, options } = hierarchy;

        // For each parent option and its child options
        Object.entries(options).forEach(([parentOption, childOptions]) => {
          // Add a row for each child option
          childOptions.forEach((childOption) => {
            const escapedParentField = escapeCSVValue(parentFieldName);
            const escapedChildField = escapeCSVValue(childFieldName);
            const escapedParentOption = escapeCSVValue(parentOption);
            const escapedChildOption = escapeCSVValue(childOption);

            rows.push(
              `${escapedParentField},${escapedChildField},${escapedParentOption},${escapedChildOption}`,
            );
          });
        });
      });

      const csvContent = rows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${selectedModel.name || "model"}_hierarchy.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      dispatch(
        createToast({
          type: "success",
          message: "Hierarchy CSV downloaded successfully",
          timeout: TOAST_TIMEOUT,
        }),
      );
    } catch (error) {
      console.error("Download error:", error);
      dispatch(
        createToast({
          type: "error",
          message: `Failed to download hierarchy: ${error instanceof Error ? error.message : "Unknown error"}`,
          timeout: TOAST_TIMEOUT,
        }),
      );
    }
  };

  return (
    <Modal title="Label Hierarchy" open={isOpen} onClose={onClose} width="560px">
      <Column spacing="300" className="label-hierarchy-modal-content">
        {error && <Alert type="error">{error}</Alert>}

        {activeTab === "upload" && (
          <>
            <Heading level={3} type="h300">
              Create Field Hierarchy
            </Heading>
            {!isAdminOrEngineer && (
              <Alert type="warning">
                You need admin or engineer permissions to manage field hierarchies.
              </Alert>
            )}
            <Column spacing="300">
              <Column spacing="100">
                <Text type="b200" color="secondary">
                  Upload a CSV file to define label hierarchies for this model. Each row defines one
                  child option for a parent-child relationship.
                </Text>
              </Column>

              <FileInput
                type="single"
                accept=".csv,.txt"
                disabled={isUploading || !isAdminOrEngineer}
                uploadButtonLabel="Select CSV file"
                uploadButtonType="secondary"
                onFileAttached={handleFileChange}
              />

              {file && (
                <Row alignmentVertical="center" spacing="200">
                  <Text type="b200" color="primary">
                    CSV processed in browser
                  </Text>
                </Row>
              )}
            </Column>

            {previewData && previewCounts && (
              <Column className="csv-preview hierarchy-view" spacing="300">
                <Text type="b200">CSV File Preview</Text>
                <Text type="b200" color="secondary">
                  {previewCounts.totalRows} relationships will be added to the model.
                </Text>

                {/* Show preview in the same format as hierarchy view */}
                {Object.entries(previewCounts.parentChildPairs).length > 1 &&
                  Object.entries(previewCounts.parentChildPairs).map(
                    ([parentField, childField], index) => (
                      <Button
                        key={index}
                        onClick={() => setActiveHierarchyIndex(index)}
                        type={activeHierarchyIndex === index ? "primary" : "tertiary"}
                        size="small"
                      >
                        {parentField} → {childField}
                      </Button>
                    ),
                  )}

                {Object.entries(previewCounts.parentChildPairs)[
                  activeHierarchyIndex < Object.entries(previewCounts.parentChildPairs).length
                    ? activeHierarchyIndex
                    : 0
                ] && (
                  <Column className="hierarchy-content" spacing="100">
                    {/* Group by parent option to match hierarchy view format */}
                    {(() => {
                      const currentPair = Object.entries(previewCounts.parentChildPairs)[
                        activeHierarchyIndex < Object.entries(previewCounts.parentChildPairs).length
                          ? activeHierarchyIndex
                          : 0
                      ];
                      const parentField = currentPair[0];
                      const childField = currentPair[1];

                      // Group by parent option
                      const groupedByParent: { [parentOption: string]: string[] } = {};

                      previewData
                        .filter(
                          (row) =>
                            row.parentFieldName === parentField &&
                            row.childFieldName === childField,
                        )
                        .forEach((row) => {
                          if (!groupedByParent[row.parentOption]) {
                            groupedByParent[row.parentOption] = [];
                          }
                          groupedByParent[row.parentOption].push(row.childOption);
                        });

                      return Object.entries(groupedByParent).map(
                        ([parentOption, childOptions], relIndex) => (
                          <Column
                            key={relIndex}
                            spacingInset="200"
                            className="hierarchy-relationship"
                          >
                            <Text type="b200" className="relationship-parent">
                              <strong>Parent Option: {parentOption}</strong>
                            </Text>
                            <Row>
                              <Text type="b200" className="relationship-children-header">
                                Child Options:
                              </Text>
                              <Text type="b200" color="secondary" className="relationship-children">
                                {childOptions.join(", ")}
                              </Text>
                            </Row>
                          </Column>
                        ),
                      );
                    })()}
                  </Column>
                )}
              </Column>
            )}

            <Column className="csv-format-info" spacing="100">
              <Text type="b200">CSV Format</Text>
              <Text type="b200" color="secondary">
                Each row defines one child option for a parent-child relationship:
              </Text>
              <Text type="b200" className="code-example">
                parentFieldName,childFieldName,parentOption,childOption
              </Text>
              <Text type="b200" color="secondary">
                Example: CategoryType,SubCategory,Hardware,Laptop
              </Text>
            </Column>

            <Button
              onClick={handleDownloadTemplate}
              type="tertiary"
              size="small"
              disabled={disabled || isUploading || !isAdminOrEngineer}
            >
              Download Template
            </Button>
          </>
        )}

        {activeTab === "view" && (
          <Column className="hierarchy-view" spacing="300">
            <Row width="100%" alignmentHorizontal={"start"} spacing="300">
              <Column width="70%">
                <Heading level={3} type="h300">
                  View Field Hierarchy
                </Heading>
              </Column>
              <Column width="30%" alignmentHorizontal="end">
                <Row spacing="200">
                  {!isEmptyHierarchy && (
                    <Button onClick={handleDownloadHierarchyCSV} type="tertiary" size="small">
                      <Icon tokens={downloadTokens}></Icon>
                      Download CSV
                    </Button>
                  )}
                  {isAdminOrEngineer && (
                    <Button onClick={() => setActiveTab("upload")} type="secondary" size="small">
                      Update Hierarchy
                    </Button>
                  )}
                </Row>
              </Column>
            </Row>
            <Text type="b200" color="secondary">
              Current label hierarchies defined for this model:
            </Text>

            {isEmptyHierarchy ? (
              <Text type="b200" color="secondary" className="no-data-message">
                {"message" in formattedHierarchy[0]
                  ? formattedHierarchy[0].message
                  : "No hierarchy data available."}
              </Text>
            ) : (
              <>
                {(formattedHierarchy as HierarchyItem[]).length > 1 &&
                  (formattedHierarchy as HierarchyItem[]).map((hierarchy, index) => (
                    <Button
                      key={index}
                      onClick={() => setActiveHierarchyIndex(index)}
                      type={activeHierarchyIndex === index ? "primary" : "tertiary"}
                      size="small"
                    >
                      {hierarchy.parentFieldName} → {hierarchy.childFieldName}
                    </Button>
                  ))}

                {/* Active Hierarchy Display */}
                {(formattedHierarchy as HierarchyItem[])[activeHierarchyIndex] && (
                  <Column className="hierarchy-content" spacing="100">
                    {(formattedHierarchy as HierarchyItem[])[
                      activeHierarchyIndex
                    ].relationships.map((rel: RelationshipItem, relIndex: number) => (
                      <Column key={relIndex} spacingInset="200" className="hierarchy-relationship">
                        <Text type="b200" className="relationship-parent">
                          <strong>Parent Option: {rel.parentOption}</strong>
                        </Text>
                        <Row>
                          <Text type="b200" className="relationship-children-header">
                            Child Options:
                          </Text>
                          <Text type="b200" color="secondary" className="relationship-children">
                            {rel.childOptions.join(", ")}
                          </Text>
                        </Row>
                      </Column>
                    ))}
                  </Column>
                )}
              </>
            )}
          </Column>
        )}

        <Row spacing="300" alignmentHorizontal="end">
          <Button onClick={onClose} type="tertiary">
            Close
          </Button>
          {activeTab === "upload" && isAdminOrEngineer && (
            <Button
              onClick={handleUpload}
              disabled={!file || isUploading || disabled}
              type="primary"
            >
              Upload
            </Button>
          )}
        </Row>
      </Column>
    </Modal>
  );
};

export default LabelHierarchyModal;
